import { useState } from "react";
import { Plus, Trash2, GripVertical, ListChecks, ClipboardList, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { format, isToday, isTomorrow, isPast, isBefore, addDays } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { useTodoLists } from "@/hooks/useTodoLists";
import { useTodoListOperations } from "@/hooks/useTodoListOperations";
import { DeleteConfirmation } from "./DeleteConfirmation";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "@/lib/utils";
import { usePermissions } from '@/contexts/PermissionsContext';

interface Task {
  id: string;
  text: string;
  completed: boolean;
  deadline?: Date | null;
  list_id: string;
  created_at: string;
  updated_at: string;
}

interface TodoList {
  id: string;
  title: string;
  room_id: string;
  created_at: string;
  updated_at: string;
  tasks: Task[];
}

// Helper function to format deadline
const formatDeadline = (date: Date): string => {
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  if (isBefore(date, new Date())) return 'Overdue';
  return format(date, 'MMM d, yyyy');
};

// Helper function to get deadline status
const getDeadlineStatus = (deadline: Date) => {
  if (isBefore(deadline, new Date())) {
    return { color: 'text-red-500', label: 'Overdue' };
  } else if (isToday(deadline)) {
    return { color: 'text-amber-500', label: 'Today' };
  } else if (isBefore(deadline, addDays(new Date(), 3))) {
    return { color: 'text-amber-400', label: 'Upcoming' };
  }
  return { color: 'text-slate-500', label: 'Later' };
};

interface TodoViewProps {
  roomId: string;
}

export const TodoView = ({ roomId }: TodoViewProps) => {
  const { permissions } = usePermissions();
  const isReadOnly = !(permissions?.can_write_todo);
  const { 
    lists, 
    loading, 
    error, 
    addList, 
    updateList, 
    deleteList, 
    addTask, 
    updateTask, 
    deleteTask, 
    toggleTask 
  } = useTodoLists(roomId);

  const { loading: isDeleting } = useTodoListOperations(roomId);

  // State for new list title and delete confirmation
  const [newListTitle, setNewListTitle] = useState("");
  const [listToDelete, setListToDelete] = useState<string | null>(null);
  const [newTaskTexts, setNewTaskTexts] = useState<Record<string, string>>({});
  const [taskDeadlines, setTaskDeadlines] = useState<Record<string, Date | null>>({});

  if (loading && !lists.length) {
    return (
      <div className="h-full p-7 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full p-7 flex flex-col items-center justify-center text-red-500">
        <p className="text-lg font-medium mb-2">Error loading todo lists</p>
        <p className="text-sm text-slate-600">
          {error instanceof Error ? error.message : String(error)}
        </p>
      </div>
    );
  }

  const addNewList = async () => {
    if (!newListTitle.trim()) return;
    try {
      const success = await addList(newListTitle);
      if (success) {
        setNewListTitle("");
        toast.success('List created successfully');
      } else {
        toast.error('Failed to create list');
      }
    } catch (error) {
      console.error('Error creating list:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create list');
    }
  };

  const handleAddTask = async (listId: string) => {
    const taskText = newTaskTexts[listId];
    if (!taskText?.trim()) return;

    const deadline = taskDeadlines[listId];
    await addTask(listId, taskText, deadline || undefined);
    
    // Clear the input and deadline
    setNewTaskTexts(prev => ({ ...prev, [listId]: '' }));
    setTaskDeadlines(prev => ({ ...prev, [listId]: null }));
  };

  const handleToggleTask = async (listId: string, taskId: string, completed: boolean) => {
    await toggleTask(listId, taskId, completed);
  };

  // Delete list handler
  const confirmDeleteList = (listId: string) => {
    setListToDelete(listId);
  };

  const handleDeleteList = async () => {
    if (!listToDelete) return;
    
    try {
      await deleteList(listToDelete);
      toast.success('List and its tasks deleted successfully');
    } catch (error) {
      console.error('Error deleting list:', error);
      toast.error('Failed to delete list');
    } finally {
      setListToDelete(null);
    }
  };

  return (
    <div className="h-full p-7 overflow-auto bg-slate-50 text-slate-800">
      <div className="mb-6">
        <h2 className="text-3xl font-semibold text-slate-700 mb-6 flex items-center"><ListChecks className="w-8 h-8 mr-3 text-slate-700" /> My Tasks</h2>
        
        {/* Add new list */}
        <div className="flex space-x-2 mb-6">
          <Input
            placeholder="Create a new list..."
            value={newListTitle}
            onChange={(e) => setNewListTitle(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addNewList()}
            className="flex-1"
            disabled={isReadOnly}
          />
          <Button 
            onClick={addNewList}
            className="bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold"
            disabled={isReadOnly}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add List
          </Button>
        </div>
      </div>

      {/* Todo Lists Grid */}
      {lists.length > 0 || loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {lists.map((list) => (
            <div
              key={list.id}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-slate-200 flex flex-col"
            >
              {/* List Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg text-slate-800 flex items-center">
                  <GripVertical className="w-5 h-5 text-slate-400 mr-2" />
                  <span>{list.title}</span>
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                  onClick={() => confirmDeleteList(list.id)}
                  disabled={isReadOnly || isDeleting}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Tasks */}
              <div className="space-y-0 mb-4 flex-grow">
                {list.tasks.map((task) => (
                  <div
                    key={`task-${list.id}-${task.id}`}
                    className={`group flex items-start space-x-3 py-3 border-b border-slate-100 last:border-b-0 ${task.completed ? 'opacity-70' : ''}`}
                  >
                    <div className="pt-1">
                      <Checkbox 
                        id={`task-${task.id}`}
                        checked={task.completed}
                        onCheckedChange={(checked) => handleToggleTask(list.id, task.id, !!checked)}
                        className="w-5 h-5 rounded-md border-slate-300 data-[state=checked]:bg-slate-800 data-[state=checked]:border-slate-800"
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor={`task-${task.id}`}
                        className={`block text-sm ${task.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}
                      >
                        {task.text}
                      </label>
                      {task.deadline && (
                        <div className={`flex items-center mt-1 text-xs ${getDeadlineStatus(new Date(task.deadline)).color}`}>
                          <Clock className="w-3 h-3 mr-1" />
                          <span>{formatDeadline(new Date(task.deadline))}</span>
                        </div>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => deleteTask(task.id)}
                      className="w-6 h-6 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                      disabled={isReadOnly}
                    >
                      <Trash2 className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Add new task */}
              <div className="space-y-2 mt-4">
                <div className="flex space-x-2">
                  <Input
                    placeholder="Add a new task..."
                    value={newTaskTexts[list.id] || ''}
                    onChange={(e) => setNewTaskTexts(prev => ({
                      ...prev,
                      [list.id]: e.target.value
                    }))}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTask(list.id)}
                    className="flex-1 text-sm"
                    disabled={isReadOnly}
                  />
                  <Button
                    onClick={() => handleAddTask(list.id)}
                    size="sm"
                    className="bg-slate-800 hover:bg-slate-700 text-slate-100"
                    disabled={isReadOnly}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center space-x-2">
                  <Popover>
                    <PopoverTrigger asChild disabled={isReadOnly}>
                      <Button
                        variant="outline"
                        size="sm"
                        className={`text-xs h-8 w-full justify-start text-left font-normal ${taskDeadlines[list.id] ? 'text-slate-800' : 'text-slate-500'}`}
                        disabled={isReadOnly}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {taskDeadlines[list.id] 
                          ? format(taskDeadlines[list.id]!, 'MMM d, yyyy')
                          : 'Set deadline (optional)'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={taskDeadlines[list.id] || undefined}
                        onSelect={(date) => {
                          setTaskDeadlines(prev => ({
                            ...prev,
                            [list.id]: date || null
                          }));
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {taskDeadlines[list.id] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTaskDeadlines(prev => ({
                          ...prev,
                          [list.id]: null
                        }));
                      }}
                      className="h-8 w-8 p-0"
                      disabled={isReadOnly}
                    >
                      <Trash2 className="h-4 w-4 text-slate-500" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Task count */}
              <div className="mt-4 text-xs text-slate-500 text-right pr-1">
                {list.tasks.filter(t => !t.completed).length} remaining
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-16 flex flex-col items-center justify-center text-slate-500 p-6 text-center">
          <ClipboardList className="w-16 h-16 mb-4 text-slate-300" />
          <h3 className="text-xl font-semibold mb-2 text-slate-600">No lists yet!</h3>
          <p className="text-slate-500 mb-4">Get started by creating your first list above.</p>
          <div className="w-64">
            <div className="flex space-x-2">
              <Input
                placeholder="My Todo List"
                value={newListTitle}
                onChange={(e) => setNewListTitle(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addNewList()}
                className="flex-1"
                disabled={isReadOnly}
              />
              <Button 
                onClick={addNewList}
                className="bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold"
                disabled={isReadOnly}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        isOpen={!!listToDelete}
        onClose={() => setListToDelete(null)}
        onConfirm={handleDeleteList}
        isLoading={isDeleting}
        title="Delete List"
        description="Are you sure you want to delete this list and all its tasks? This action cannot be undone."
      />
    </div>
  );
};
